import { supabase } from "./supabase";
import { logError } from "./logError";

/**
 * Initiate a PDF export for a specific form via Edge Function
 * @param appId - The Dashpivot app ID
 * @param formId - The specific form ID to export
 * @returns The export ID for polling
 */
export async function initiatePdfExport(
  appId: string,
  formId: string
): Promise<string> {
  const { data, error } = await supabase.functions.invoke("export-pdf", {
    body: { appId, formId, action: "initiate" },
  });

  if (error || !data?.exportId) {
    throw new Error(error?.message || "Failed to initiate PDF export");
  }

  return data.exportId;
}

/**
 * Poll the export status until complete or failed via Edge Function
 * @param exportId - The export ID to check
 * @returns The download URL when complete
 */
export async function pollExportStatus(
  exportId: string,
  maxAttempts = 60, // 60 attempts x 3s = 180 seconds (3 minutes) timeout
  intervalMs = 3000
): Promise<string> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const { data, error } = await supabase.functions.invoke("export-pdf", {
      body: { exportId, action: "status" },
    });

    if (error) {
      throw new Error(error.message || "Failed to check export status");
    }

    if (data.status === "complete" && data.url) {
      return data.url;
    }

    if (data.status === "failed") {
      throw new Error(`Export failed: ${data.error || "Unknown error"}`);
    }

    // Wait before next poll
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }

  throw new Error("Export timed out");
}

/**
 * Download a PDF from the given URL by opening in new tab
 * (Direct fetch blocked by CORS - Dashpivot doesn't set Access-Control headers)
 * @param url - The PDF download URL
 * @param _filename - Unused, kept for API compatibility
 */
 
export function downloadPdf(url: string, _filename?: string): void {
  // Open the PDF URL in a new browser tab
  // The browser will handle the download or display based on user settings
  window.open(url, "_blank");
}

/**
 * Full export flow: initiate, poll, and download
 * @param appId - Dashpivot app ID
 * @param formId - Form ID to export
 * @param docketNo - Docket number for filename
 */
export async function exportFormAsPdf(
  appId: string,
  formId: string,
  docketNo: string,
  onProgress?: (status: string) => void
): Promise<void> {
  try {
    onProgress?.("Initiating export...");
    const exportId = await initiatePdfExport(appId, formId);

    onProgress?.("Generating PDF...");
    const downloadUrl = await pollExportStatus(exportId);

    onProgress?.("Opening PDF...");
    downloadPdf(downloadUrl, `${docketNo}-variation.pdf`);

    onProgress?.("Complete!");
  } catch (error) {
    logError("PDF export error:", error);
    throw error;
  }
}
